// hook dependancies
import { useGlobal, setGlobal } from "reactn";

// helpers
import { getPhoneNumberValues } from "../components/_common/mobileGroup/Mobile.helpers";

/**
 * Address Hooks
 */
const Hook = () => {
    const [values] = useGlobal("values");

    const setGlobalValue = (id, value) => {
        setGlobal({
            values: { ...values, [id]: value }
        });
    };

    const [addressObj] = useGlobal("addressObj");

    const setAddress = (key, obj) => {
        const address = {
            addressLine1: obj.reformattedAddress[1] ? obj.reformattedAddress[1] : "",
            addressLine2: obj.reformattedAddress[2] ? obj.reformattedAddress[2] : "",
            addressLine3: obj.reformattedAddress[3] ? obj.reformattedAddress[3] : "",
            addressLine4: "",
            eircode: obj.postcode,
            town: obj.reformattedAddress[4] ? obj.reformattedAddress[4] : "",
            city: "",
            county: obj.reformattedAddress[5] ? obj.reformattedAddress[5] : "",
            country: "Ireland",
            postCode: "",
            organisationName: obj.reformattedAddress[0] ? obj.reformattedAddress[0] : "",
            autoAddressId: obj.addressId,
            lookupReference: obj.addressId,
            internationalAddress: false,
            addressValidated: true,
            ciamAddressId: obj.ciamAddressId
        };
        setGlobal({
            addressObj: { ...addressObj, [key]: address }
        });
    };

    // update receiver address when editing
    const updateAddressObject = (key, obj) => {
        setGlobal({
            addressObj: { ...addressObj, [key]: obj }
        });
    };

    // clear add address
    const clearAddress = key => {
        setGlobal({
            addressObj: { ...addressObj, [key]: null }
        });
    };

    // request headers for authorise API requests
    const getRequestParamsforAuthoriseAPI = request => {
        return {
            addressLine1: request.addressLine1,
            addressLine2: request.addressLine2,
            addressLine3: request.addressLine3,
            addressLine4: request.addressLine4,
            eircode: request.eircode,
            town: request.town,
            city: request.city,
            county: request.county,
            country: request.country,
            postCode: request.postCode,
            organisationName: request.organisationName,
            autoAddressId: request.autoAddressId,
            internationalAddress: request.internationalAddress,
            addressValidated: request.addressValidated
        };
    };

    // request headers for CIAM API requests
    const getRequestParamsforCIAM = request => {
        return {
            UID: request.UID,
            displayName: request.displayName,
            defaultAddress: request.defaultAddress,
            country: request.country,
            town: request.town,
            addressLine1: request.addressLine1,
            addressLine2: request.addressLine2,
            addressLine3: request.addressLine3,
            addressLine4: request.addressLine4,
            city: request.city,
            county: request.county,
            lookupReference: request.lookupReference,
            postcode: request.postcode,
            eircode: request.eircode,
            organisationName: request.organisationName,
            id: request.id
        };
    };

    // returns all the values related to the particular address
    const getAllAddressValues = (addressId, newValues) => {
        return newValues.manualEntry || newValues[`${addressId}NatOrInternational`] === "international"
            ? {
                  UID: newValues.ciamUserId,
                  id: newValues[`${addressId}EditId`],
                  displayName: newValues[`${addressId}Alias`],
                  defaultAddress: newValues[`${addressId}DefaultForced`] || newValues[`${addressId}Default`],
                  addressLine1: newValues[`${addressId}Addresslineone`] ? newValues[`${addressId}Addresslineone`] : "",
                  addressLine2: newValues[`${addressId}Addresslinetwo`] ? newValues[`${addressId}Addresslinetwo`] : "",
                  addressLine3: newValues[`${addressId}Addresslinethree`] ? newValues[`${addressId}Addresslinethree`] : "",
                  addressLine4: newValues[`${addressId}Addresslinefour`] ? newValues[`${addressId}Addresslinefour`] : "",
                  eircode: newValues[`${addressId}Eircode`] && newValues[`${addressId}Country`] === "Ireland" ? newValues[`${addressId}Eircode`] : "",
                  town: newValues[`${addressId}Town`] ? newValues[`${addressId}Town`] : "",
                  city: newValues[`${addressId}City`] ? newValues[`${addressId}City`] : "",
                  county: newValues[`${addressId}County`] ? newValues[`${addressId}County`] : "",
                  country: newValues[`${addressId}Country`] ? newValues[`${addressId}Country`] : "",
                  postCode: newValues[`${addressId}PostCode`] && newValues[`${addressId}Country`] !== "Ireland" ? newValues[`${addressId}PostCode`] : "",
                  postcode: newValues[`${addressId}PostCode`] && newValues[`${addressId}Country`] !== "Ireland" ? newValues[`${addressId}PostCode`] : "",
                  organisationName: "",
                  autoAddressId: "",
                  internationalAddress: newValues[`${addressId}Country`] !== "Ireland",
                  addressValidated: false
              }
            : {
                  ...addressObj[addressId],
                  UID: newValues.ciamUserId,
                  id: newValues[`${addressId}EditId`],
                  displayName: newValues[`${addressId}Alias`],
                  defaultAddress: newValues[`${addressId}DefaultForced`] || newValues[`${addressId}Default`]
              };
    };

    // wrapper function that returns address request headers to be passed to APIs
    const getAddress = (addressId, newValues, settings) => {
        // get all the address values available
        const addressValues = getAllAddressValues(addressId, newValues);

        // if you just need authorise API values,
        if (settings) {
            // if it is an authorise API request
            if (settings.authoriseAPI) {
                return getRequestParamsforAuthoriseAPI(addressValues);
            }

            // if it is a CIAM API request
            if (settings.ciamAPI) {
                return getRequestParamsforCIAM(addressValues);
            }
        }

        return addressValues;
    };

    const createAddressSummary = selectedAddress => {
        const address = [];

        if (selectedAddress.organisationName) address.push(selectedAddress.organisationName);
        if (selectedAddress.addressLine1) address.push(selectedAddress.addressLine1);
        if (selectedAddress.addressLine2) address.push(selectedAddress.addressLine2);
        if (selectedAddress.addressLine3) address.push(selectedAddress.addressLine3);
        if (selectedAddress.addressLine4) address.push(selectedAddress.addressLine4);
        if (selectedAddress.city) address.push(selectedAddress.city);
        if (selectedAddress.town) address.push(selectedAddress.town);
        if (selectedAddress.county) address.push(selectedAddress.county);
        if (selectedAddress.eircode && selectedAddress.country.toUpperCase() === "IRELAND") address.push(selectedAddress.eircode);
        if (selectedAddress.postcode && selectedAddress.country.toUpperCase() !== "IRELAND") address.push(selectedAddress.postcode);
        if (selectedAddress.country) address.push(selectedAddress.country);

        return address;
    };

    const setContactValues = contact => {
        const mobileNumber = getPhoneNumberValues(contact.receiver.contactNumber);
        setGlobal({
            values: {
                ...values,
                contactFirstName: contact.receiver.firstName,
                contactLastName: contact.receiver.lastName,
                contactEmailAddress: contact.receiver.emailAddress,
                contactMobile: contact.receiver.contactNumber,
                contactMobileCountryCode: mobileNumber.countryCode.replace(/\s/g, ""),
                contactMobilePrefix: mobileNumber.prefix.replace(/\s/g, ""),
                contactMobileNumber: mobileNumber.subNumber.replace(/\s/g, ""),
                contactBusinessName: contact.receiver.companyName,
                contactAddress: contact.receiverAddress.country === "Ireland" ? "addressSet" : "",
                contactReceiverId: contact.receiverId,
                contactAddressAddresslineone: contact.receiverAddress.addressLine1,
                contactAddressAddresslinetwo: contact.receiverAddress.addressLine2,
                contactAddressAddresslinethree: contact.receiverAddress.addressLine3,
                contactAddressAddresslinefour: contact.receiverAddress.addressLine4,
                contactAddressEircode: contact.receiverAddress.eircode,
                contactAddressPostCode: contact.receiverAddress.postcode,
                contactAddressCounty: contact.receiverAddress.county,
                contactAddressCity: contact.receiverAddress.city,
                contactAddressTown: contact.receiverAddress.town,
                contactAddressintCountry: contact.receiverAddress.country,
                contactAddressNatOrInternational: contact.receiverAddress.country === "Ireland" ? "national" : "international"
            }
        });
    };

    const resetContactAddressData = () => {
        setGlobal({
            values: {
                ...values,
                contactFirstName: "",
                contactLastName: "",
                contactEmailAddress: "",
                contactMobile: "",
                contactMobileCountryCode: "IE-353",
                contactMobilePrefix: "",
                contactMobileNumber: "",
                contactReceiverId: "",
                contactAddress: "",
                contactBusinessName: "",
                contactAddressAddresslineone: "",
                contactAddressAddresslinetwo: "",
                contactAddressAddresslinethree: "",
                contactAddressAddresslinefour: "",
                contactAddressPostCode: "",
                contactAddressEircode: "",
                contactAddressCounty: "",
                contactAddressCity: "",
                contactAddressintCountry: "",
                contactAddressTown: "",
                contactAddressNatOrInternational: "national"
            }
        });
    };

    // this sets manual and international addresses for an instance of the AddressTemplate
    // it sets fields related to international and manual addresses
    const setAdditionalAddressVals = (id, address, currentVals) => {
        const irishAddress = address.country ? address.country.toUpperCase() === "IRELAND" : false;

        const updatedVals = {
            ...currentVals,
            [`${id}Alias`]: `${address.displayName}`,
            [`${id}AliasDisabled`]: !!(address.displayName === "Home" || address.displayName === "Business" || address.displayName === "Alternate"),
            [`${id}`]: irishAddress ? "addressSet" : "",
            [`${id}Default`]: address.defaultAddress,
            [`${id}DefaultDisabled`]: address.defaultAddress,
            [`${id}NatOrInternational`]: address.country && address.country.toUpperCase() !== "IRELAND" ? "international" : "national",
            [`${id}Addresslineone`]: address.addressLine1,
            [`${id}Addresslinetwo`]: address.addressLine2,
            [`${id}Addresslinethree`]: address.addressLine3,
            [`${id}Addresslinefour`]: address.addressLine4,
            [`${id}Eircode`]: address.eircode,
            [`${id}PostCode`]: address.postcode,
            [`${id}County`]: address.county,
            [`${id}Country`]: address.country,
            [`${id}City`]: address.city,
            [`${id}Town`]: address.town,
            [`${id}EditId`]: address.id
        };
        setGlobal({
            values: updatedVals
        });

        return updatedVals;
    };

    const resetPersonalAddressData = forceDefaultAddress => {
        setGlobal({
            values: {
                ...values,
                personalAddressAlias: "",
                personalAddressAliasDisabled: "",
                personalAddress: "",
                personalAddressAddresslineone: "",
                personalAddressAddresslinetwo: "",
                personalAddressDefaultDisabled: "",
                personalAddressDefault: false,
                personalAddressDefaultForced: forceDefaultAddress,
                personalAddressAddresslinethree: "",
                personalAddressAddresslinefour: "",
                personalAddressEircode: "",
                personalAddressPostCode: "",
                personalAddressCounty: "",
                personalAddressCity: "",
                personalAddressintCountry: "",
                personalAddressTown: "",
                personalAddressNatOrInternational: "national"
            }
        });
    };

    return {
        setGlobalValue,
        setAddress,
        getAddress,
        clearAddress,
        setContactValues,
        resetPersonalAddressData,
        resetContactAddressData,
        addressObj,
        values,
        createAddressSummary,
        updateAddressObject,
        getRequestParamsforAuthoriseAPI,
        getRequestParamsforCIAM,
        setAdditionalAddressVals
    };
};

export default Hook;
